import { z } from "zod";

// Form schemas for different QR code types
export const urlSchema = z.object({
  type: z.literal("url"),
  url: z.string().trim().url(),
});

export const vCardSchema = z.object({
  type: z.literal("vcard"),
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  phone: z.string().trim().min(1),
  email: z.string().email(),
  organization: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  country: z.string().optional(),
});

export const phoneSchema = z.object({
  type: z.literal("phone"),
  phone: z.string().trim().min(1),
});

export const smsSchema = z.object({
  type: z.literal("sms"),
  phone: z.string().trim().min(1),
  message: z.string().optional(),
});

export const emailSchema = z.object({
  type: z.literal("email"),
  email: z.string().email(),
  subject: z.string().optional(),
  body: z.string().optional(),
});

export const wifiSchema = z.object({
  type: z.literal("wifi"),
  ssid: z.string().trim().min(1),
  password: z.string(),
  encryption: z.enum(["WPA", "WEP", "nopass"]),
});

export const cryptoSchema = z.object({
  type: z.literal("crypto"),
  cryptoType: z.enum(["bitcoin", "ethereum"]),
  address: z.string().trim().min(1),
  amount: z.string().optional(),
  message: z.string().optional(),
});

export const geoSchema = z.object({
  type: z.literal("geo"),
  latitude: z.string().trim().min(1).refine((value) => Number(value) >= -90 && Number(value) <= 90),
  longitude: z.string().trim().min(1).refine((value) => Number(value) >= -180 && Number(value) <= 180),
});

export const eventSchema = z.object({
  type: z.literal("event"),
  summary: z.string().trim().min(1),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  description: z.string().optional(),
});

export const textSchema = z.object({
  type: z.literal("text"),
  text: z.string().min(1),
});

export const mecardSchema = z.object({
  type: z.literal("mecard"),
  name: z.string().trim().min(1),
  phone: z.string().trim().min(1),
  email: z.union([z.literal(""), z.string().email()]).optional(),
});

const socialSchema = z.object({
  type: z.literal("social"),
  platform: z.enum([
    "x",
    "instagram",
    "facebook",
    "linkedin",
    "github",
    "tiktok",
    "youtube",
    "twitch",
    "bluesky"
  ]),
  username: z.string().trim().min(1),
});

export const formSchema = z.discriminatedUnion("type", [
  urlSchema,
  vCardSchema,
  phoneSchema,
  smsSchema,
  emailSchema,
  wifiSchema,
  cryptoSchema,
  geoSchema,
  eventSchema,
  textSchema,
  mecardSchema,
  socialSchema,
]);

export type SocialPlatform = "x" | "instagram" | "facebook" | "linkedin" | "github" | "tiktok" | "youtube" | "twitch" | "bluesky";

export type QRFormValues = z.infer<typeof formSchema>;
